import {
  ArrowUpDown,
  BarChart3,
  Bell,
  CircleDot,
  Clock,
  FileStack,
  Flag,
  FolderOpen,
  Gauge,
  Group,
  KeyRound,
  LayoutTemplate,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListTodo,
  Package,
  Shield,
  Star,
  Tag,
  type LucideIcon,
} from 'lucide-react';
import { P, type PermissionKey } from '@clarix/rbac';

/**
 * Ported verbatim from clario-frontend src/utils/constants/settingTab.constant.ts
 * (hrefs resolve to /projects/settings?tab=<id>). Drives the Settings sidebar
 * panel. `superAdminOnly` is approximated by the required permission in the
 * monorepo (no SUPER_ADMIN role on the auth store yet).
 *
 * ⚠️ THIS IS A DUPLICATE of apps/projects .../utils/constants/settingTab.constant.ts,
 * and the two MUST be kept in step by hand. The shell and the Projects remote are
 * separate Module Federation builds, so neither can import the other's list — the
 * shell renders the sidebar panel from THIS file while the page renders the tab
 * BODY from that one.
 *
 * Adding a tab to only one of them is the failure this comment exists to prevent,
 * and it is not hypothetical: the `api-access` tab was added to the page first and
 * simply never appeared in the sidebar, because the panel is built from here.
 * `settings-tabs.parity.test.ts` now compares the two files and fails if they
 * diverge. Add a tab to BOTH, or delete it from BOTH.
 */
export interface SettingsTabConfig {
  id: string;
  label: string;
  section: string;
  icon: LucideIcon;
  requiredPermission?: PermissionKey;
}

export const SETTING_TABS: SettingsTabConfig[] = [
  // General
  { id: 'roles', label: 'Roles & Permissions', icon: Shield, section: 'General', requiredPermission: P.SETTINGS_MANAGE_GENERAL },
  { id: 'notifications', label: 'Notifications & Emails', icon: Bell, section: 'General', requiredPermission: P.NOTIFICATIONS_CONFIG_MANAGE },
  { id: 'api-access', label: 'API access', icon: KeyRound, section: 'General', requiredPermission: P.SETTINGS_API_KEYS_VIEW },

  // Masters
  { id: 'priorities', label: 'Priorities', icon: Flag, section: 'Masters', requiredPermission: P.SETTINGS_MANAGE_MASTERS },
  { id: 'tags', label: 'Tags', icon: Tag, section: 'Masters', requiredPermission: P.SETTINGS_MANAGE_MASTERS },

  // Project
  { id: 'project-statuses', label: 'Project Statuses', icon: CircleDot, section: 'Project', requiredPermission: P.SETTINGS_PROJECT_CONFIG },
  { id: 'project-types', label: 'Project Types', icon: Layers, section: 'Project', requiredPermission: P.SETTINGS_PROJECT_CONFIG },
  { id: 'module-statuses', label: 'Module Statuses', icon: Package, section: 'Project', requiredPermission: P.SETTINGS_PROJECT_CONFIG },
  { id: 'feature-statuses', label: 'Feature Statuses', icon: Star, section: 'Project', requiredPermission: P.SETTINGS_PROJECT_CONFIG },
  { id: 'project-templates', label: 'Project Templates', icon: LayoutTemplate, section: 'Project', requiredPermission: P.PROJECT_TEMPLATES_VIEW },

  // Configuration
  { id: 'statuses', label: 'Task Statuses', icon: CircleDot, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'task-status-categories', label: 'Task Status Categories', icon: FolderOpen, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_MASTERS },
  { id: 'task-types', label: 'Task Types', icon: Layers, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'task-type-categories', label: 'Task Type Categories', icon: FolderOpen, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_MASTERS },
  { id: 'time-entry-statuses', label: 'Time Entry Statuses', icon: Clock, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'task-view-layouts', label: 'Task View Layouts', icon: LayoutGrid, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'task-view-sorts', label: 'Task View Sort Options', icon: ArrowUpDown, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'task-view-groups', label: 'Task View Group Options', icon: Group, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'my-work-settings', label: 'My Work Settings', icon: ListTodo, section: 'Configuration', requiredPermission: P.SETTINGS_MY_WORK_MANAGE },
  { id: 'project-limits', label: 'Limits', icon: Gauge, section: 'Configuration', requiredPermission: P.SETTINGS_MANAGE_CONFIG },

  // Dashboard
  { id: 'overview-widgets', label: 'Dashboard Widgets', icon: LayoutDashboard, section: 'Dashboard', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'overview-chart-types', label: 'Chart Types', icon: BarChart3, section: 'Dashboard', requiredPermission: P.SETTINGS_MANAGE_CONFIG },
  { id: 'overview-templates', label: 'Default Templates', icon: FileStack, section: 'Dashboard', requiredPermission: P.SETTINGS_MANAGE_CONFIG },

  // Spark
];

/**
 * Every permission that opens SOMETHING inside Settings — the umbrella key plus
 * each tab's own key, deduped.
 *
 * The Settings rail icon is gated on this, any-of. Gating it on
 * `module.settings.view` alone made every per-tab key below the umbrella
 * decorative: a member granted "View API access" had the tab, had the panel
 * row, had the page — and no way to reach any of it, because the icon that
 * opens the panel was hidden. Granting the umbrella to fix that is not the
 * answer either; the panel rows treat it as "see ALL settings", so it would
 * hand over Roles & Permissions and every master list with it.
 *
 * Derived from SETTING_TABS rather than hand-listed, so a new tab opens the
 * rail for its own key with no second edit to remember.
 */
export const SETTINGS_RAIL_PERMISSIONS: PermissionKey[] = [
  ...new Set<PermissionKey>([
    P.SETTINGS_VIEW,
    ...SETTING_TABS.map((t) => t.requiredPermission).filter(
      (k): k is PermissionKey => k != null,
    ),
  ]),
];

export const SETTING_SECTIONS = [
  'General',
  'Masters',
  'Project',
  'Configuration',
  'Dashboard',
] as const;
